"use client";

import React, { useEffect, useMemo } from "react";
import { Empty, Pagination, Spinner, Text } from "ui-components";

import { Advert } from "@/components/Advert/Advert";
import { makeToast } from "@/components/Toast/Toast";
import { IEventsSearchParams } from "@/src/app/[locale]/(public)/events/page";
import { EventCard } from "@/src/components/events/EventCard";
import {
  WpPostEntity,
  WpPostsResponse,
  OrganizationGetEventsListingParams,
  useOrganizationGetEventsListing,
  getOrganizationGetEventsListingQueryKey,
  AdvertisingGetAdPlaceholderType,
} from "@/src/hooks/api/generated";
import { usePageHref } from "@/src/hooks/usePageHref";

import { SEARCH_FILTERS } from "../enums/search-filters.enum";

import { EventsListingHeader } from "./EventsListingHeader";

const PAGE_SIZE = 12;

interface IEventsListingPageProps {
  searchParams: IEventsSearchParams;
  initialEvents?: WpPostsResponse | null;
}

function cleanParams<T extends Record<string, string | undefined>>(
  parameters: T
): Partial<T> {
  return Object.entries(parameters).reduce((acc, [key, value]) => {
    if (value !== undefined && value.trim() !== "") {
      acc[key as keyof T] = value as T[keyof T];
    }
    return acc;
  }, {} as Partial<T>);
}

export function EventsListingPage({
  searchParams,
  initialEvents,
}: IEventsListingPageProps): JSX.Element {
  const page = searchParams[SEARCH_FILTERS.PAGE] ?? "1";
  const searchTerm = searchParams[SEARCH_FILTERS.SEARCH] ?? undefined;

  const boolParam = (value?: string): string | undefined =>
    value === "true" ? "true" : undefined;

  const params = useMemo(() => {
    const rawParams: OrganizationGetEventsListingParams = {
      page,
      pageSize: String(PAGE_SIZE),
      searchTerm,
      showVirtualEvents: boolParam(
        searchParams[SEARCH_FILTERS.SHOW_VIRTUAL_EVENTS]
      ),
      showInPersonEvents: boolParam(
        searchParams[SEARCH_FILTERS.SHOW_IN_PERSON_EVENTS]
      ),
      showPastEvents: boolParam(searchParams[SEARCH_FILTERS.SHOW_PAST_EVENTS]),
      showMygworkEvents: boolParam(
        searchParams[SEARCH_FILTERS.SHOW_MYGWORK_EVENTS]
      ),
    };
    return cleanParams(rawParams) as OrganizationGetEventsListingParams;
  }, [page, searchTerm, searchParams]);

  const { data: eventsData, status } =
    useOrganizationGetEventsListing<WpPostsResponse>(params, {
      query: {
        queryKey: getOrganizationGetEventsListingQueryKey(params),
        initialData: initialEvents ?? undefined,
        initialDataUpdatedAt: initialEvents ? Date.now() : undefined,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
      },
    }) as { data: WpPostsResponse; status: string };

  // Clear the not-found query param (after the toast) in an effect so it never
  // runs during render — avoids a hydration mismatch between server and client.
  useEffect(() => {
    if (searchParams[SEARCH_FILTERS.NOT_FOUND]) {
      makeToast({
        title:
          "This event is no longer available, here are some other events that may interest you.",
        intent: "info",
      });
      window.history.replaceState(null, "", `${window.location.pathname}`);
    }
  }, [searchParams]);

  const buildPageHref = usePageHref();

  return (
    <>
      <EventsListingHeader
        searchParams={searchParams}
        isLoading={searchTerm !== "" && status === "pending"}
      />
      <div className="mt-10 flex justify-center">
        <Text variant="base">Found: {eventsData?.total} events </Text>
      </div>
      <div className="my-20">
        {eventsData?.data.length === 0 && <Empty text="No news found." />}
        {status === "pending" && page === "1" && !eventsData?.data.length && (
          <div className="flex justify-center mt-5 mb-20">
            <Spinner size="lg" intent="brand-primary" />
          </div>
        )}
        {eventsData?.data.length ? (
          <>
            <div className="w-full flex flex-wrap justify-center gap-y-10 md:justify-start items-start -mx-2.5 -mt-8">
              {eventsData?.data.map((event: WpPostEntity, index: number) => (
                <React.Fragment key={event.ID}>
                  <div className="w-full sm:w-1/2 md:w-1/3">
                    <EventCard event={event} />
                  </div>
                  {index + 1 === 6 && (
                    <div className="w-full -mb-4">
                      <Advert type={AdvertisingGetAdPlaceholderType.mid_page} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            {!!eventsData?.total && eventsData?.total > 0 && (
              <div className="mt-10">
                <Pagination
                  currentPage={page ? parseInt(page) : 1}
                  totalPages={Math.ceil((eventsData?.total ?? 0) / PAGE_SIZE)}
                  totalRecords={eventsData?.total ?? 0}
                  getPageHref={(pageNumber) =>
                    buildPageHref(SEARCH_FILTERS.PAGE, pageNumber, [
                      SEARCH_FILTERS.NOT_FOUND,
                    ])
                  }
                />
              </div>
            )}
          </>
        ) : null}
      </div>
    </>
  );
}
